<template>
  <div class="product-create">
    <div class="page-header">
      <h2>创建商品</h2>
      <a-button @click="$router.back()">
        <template #icon>
          <ArrowLeftOutlined />
        </template>
        返回
      </a-button>
    </div>
    
    <a-form
      :model="form"
      :rules="rules"
      @finish="handleSubmit"
      layout="vertical"
      :scroll-to-first-error="true"
    >
      <a-row :gutter="24">
        <a-col :span="16">
          <a-card title="基本信息" class="form-card">
            <a-form-item label="商品名称" name="name">
              <a-input
                v-model:value="form.name"
                placeholder="请输入商品名称"
                maxlength="100"
                show-count
              />
            </a-form-item>
            
            <a-form-item label="商品分类" name="categoryId">
              <a-cascader
                v-model:value="form.categoryId"
                :options="categoryOptions"
                :field-names="{ label: 'name', value: 'id', children: 'children' }"
                placeholder="请选择商品分类"
                change-on-select
                :show-search="true"
                @change="onCategoryChange"
              />
            </a-form-item>
            
            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item label="销售价格" name="salePrice">
                  <a-input-number
                    v-model:value="form.salePrice"
                    placeholder="请输入销售价格"
                    :min="0"
                    :precision="2"
                    style="width: 100%"
                    addon-before="¥"
                  />
                </a-form-item>
              </a-col>
              
              <a-col :span="12">
                <a-form-item label="成本价格" name="costPrice">
                  <a-input-number
                    v-model:value="form.costPrice"
                    placeholder="请输入成本价格"
                    :min="0"
                    :precision="2"
                    style="width: 100%"
                    addon-before="¥"
                  />
                </a-form-item>
              </a-col>
            </a-row>
            
            <a-form-item label="商品备注" name="remark">
              <a-textarea
                v-model:value="form.remark"
                placeholder="请输入商品备注"
                :rows="4"
                maxlength="500"
                show-count
              />
            </a-form-item>
          </a-card>
          
          <a-card title="库存信息" class="form-card">
            <!-- 规格选择：颜色 / 尺码 chip 多选 -->
            <div class="spec-section">
              <div class="spec-row">
                <div class="spec-label">颜色</div>
                <div class="spec-chips">
                  <span
                    v-for="color in colorOptions"
                    :key="color.value"
                    class="spec-chip"
                    :class="{ on: selectedColors.includes(color.value) }"
                    @click="toggleColor(color.value)"
                  >{{ color.value }}</span>
                  <a-input
                    v-model:value="newColorValue"
                    placeholder="新增颜色"
                    size="small"
                    style="width: 110px;"
                    @keydown.enter.prevent="handleAddColor"
                  />
                  <a-button size="small" @click="handleAddColor">添加</a-button>
                </div>
              </div>
              <div class="spec-row">
                <div class="spec-label">尺码</div>
                <div class="spec-chips">
                  <span
                    v-for="size in sizeOptions"
                    :key="size.value"
                    class="spec-chip"
                    :class="{ on: selectedSizes.includes(size.value) }"
                    @click="toggleSize(size.value)"
                  >{{ size.value }}</span>
                  <a-input
                    v-model:value="newSizeValue"
                    placeholder="新增尺码"
                    size="small"
                    style="width: 110px;"
                    @keydown.enter.prevent="handleAddSize"
                  />
                  <a-button size="small" @click="handleAddSize">添加</a-button>
                </div>
              </div>
            </div>

            <a-divider />

            <!-- 初始库存：按仓库 tab 列出 SKU 录入行 -->
            <div class="stock-section">
              <div class="section-header">
                <span>初始库存</span>
                <div style="display: flex; align-items: center; gap: 16px;">
                  <div style="font-size: 14px; color: #666;">
                    共 {{ skuRows.length }} 个 SKU × {{ warehouseOptions.length }} 仓库
                  </div>
                  <div style="font-size: 14px; color: #666;">
                    总库存:
                    <a-tag :color="totalStockQuantity > 20 ? 'green' : totalStockQuantity > 0 ? 'orange' : 'red'" style="font-weight: 500;">
                      {{ totalStockQuantity }}
                    </a-tag>
                  </div>
                </div>
              </div>

              <a-radio-group
                v-if="warehouseOptions.length > 0"
                v-model:value="stockWhIndex"
                button-style="solid"
                style="margin-bottom: 12px;"
              >
                <a-radio-button
                  v-for="(wh, i) in warehouseOptions"
                  :key="wh.id"
                  :value="i"
                >{{ wh.name }}</a-radio-button>
              </a-radio-group>

              <div
                v-if="warehouseOptions[stockWhIndex]"
                style="font-size: 12px; color: #999; margin-bottom: 12px;"
              >
                正在录入「{{ warehouseOptions[stockWhIndex].name }}」的初始库存，切换仓库可分别录入
              </div>

              <div v-if="skuRows.length === 0" class="empty-stock">
                <a-empty description="请先选择颜色与尺码" />
              </div>
              <div v-else class="sku-rows">
                <div
                  v-for="(row, idx) in skuRows"
                  :key="row.key"
                  class="sku-row"
                >
                  <span class="sku-label">{{ row.label }}</span>
                  <a-input-number
                    :value="row.qty"
                    :min="0"
                    :precision="0"
                    placeholder="0"
                    style="width: 140px;"
                    @change="(v) => onSkuQty(idx, v)"
                  />
                  <span class="sku-unit">件</span>
                </div>
              </div>
            </div>
          </a-card>
        </a-col>
        
        <a-col :span="8">
          <a-card title="商品图片" class="form-card">
            <a-form-item name="images">
              <a-upload
                v-model:file-list="fileList"
                list-type="picture-card"
                :before-upload="beforeUpload"
                @preview="handlePreview"
                @remove="handleRemove"
                :multiple="true"
                accept="image/*"
              >
                <div v-if="fileList.length < 8">
                  <plus-outlined />
                  <div style="margin-top: 8px">上传图片</div>
                </div>
              </a-upload>
            </a-form-item>
          </a-card>
        </a-col>
      </a-row>
      
      <div class="form-actions">
        <a-button type="primary" html-type="submit" size="large" :loading="loading">
          创建商品
        </a-button>
        <a-button size="large" @click="$router.back()" style="margin-left: 16px">
          取消
        </a-button>
      </div>
    </a-form>
    
    <a-modal
      v-model:open="previewVisible"
      :title="previewTitle"
      :footer="null"
    >
      <img :src="previewImage" style="width: 100%" />
    </a-modal>
  </div>
</template>

<script>
import { reactive, ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  ArrowLeftOutlined,
  PlusOutlined
} from '@ant-design/icons-vue'
import { productApi, uploadApi, warehouseApi } from '@/api'

export default {
  name: 'ProductCreate',
  components: {
    ArrowLeftOutlined,
    PlusOutlined
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const categoryOptions = ref([])
    const warehouseOptions = ref([])
    const colorOptions = ref([])
    const sizeOptions = ref([])
    const fileList = ref([])
    const previewVisible = ref(false)
    const previewImage = ref('')
    const previewTitle = ref('')
    
    const form = reactive({
      name: '',
      categoryId: undefined,
      salePrice: undefined,
      costPrice: undefined,
      remark: '',
      stockItems: [],
      imageUrls: []
    })
    
    const rules = {
      name: [
        { required: true, message: '请输入商品名称', trigger: 'blur' }
      ],
      categoryId: [
        { required: true, message: '请选择商品分类', trigger: 'change' }
      ]
    }
    
    const loadCategories = async () => {
      try {
        const result = await productApi.getCategoryTree()
        categoryOptions.value = result.data
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    }
    
    const loadAttributes = async () => {
      try {
        const [colors, sizes] = await Promise.all([
          productApi.getAttributes('color'),
          productApi.getAttributes('size')
        ])
        colorOptions.value = colors.data
        sizeOptions.value = sizes.data
      } catch (error) {
        console.error('加载属性失败:', error)
      }
    }
    
    const loadWarehouses = async () => {
      try {
        const result = await warehouseApi.list()
        warehouseOptions.value = result.data || []
      } catch (error) {
        console.error('加载仓库失败:', error)
      }
    }
    
    const newColorValue = ref('')
    const newSizeValue = ref('')

    const handleAddColor = async () => {
      const val = newColorValue.value.trim()
      if (!val) return
      if (colorOptions.value.some(c => c.value === val)) {
        message.warning('该颜色已存在')
        return
      }
      try {
        const allColors = [...colorOptions.value, { value: val, type: 'color', orderNum: colorOptions.value.length + 1, status: 1 }]
        await productApi.batchUpdateAttributes({
          attributes: allColors.map((c, i) => ({ type: 'color', value: c.value, orderNum: i + 1, status: 1 }))
        })
        colorOptions.value = allColors
        newColorValue.value = ''
        message.success(`颜色"${val}"添加成功`)
      } catch (error) {
        message.error('添加颜色失败')
      }
    }

    const handleAddSize = async () => {
      const val = newSizeValue.value.trim()
      if (!val) return
      if (sizeOptions.value.some(s => s.value === val)) {
        message.warning('该尺寸已存在')
        return
      }
      try {
        const allSizes = [...sizeOptions.value, { value: val, type: 'size', orderNum: sizeOptions.value.length + 1, status: 1 }]
        await productApi.batchUpdateAttributes({
          attributes: allSizes.map((s, i) => ({ type: 'size', value: s.value, orderNum: i + 1, status: 1 }))
        })
        sizeOptions.value = allSizes
        newSizeValue.value = ''
        message.success(`尺寸"${val}"添加成功`)
      } catch (error) {
        message.error('添加尺寸失败')
      }
    }

    // ───────── 矩阵式 SKU 编辑（颜色 × 尺码，每仓库分别录入） ─────────
    // 与小程序 product-create / Edit.vue 一致：勾选颜色/尺码，SKU 为它们的笛卡尔积。
    // 提交时按 warehouseList × colors × sizes 全展开（含 qty=0）。
    const selectedColors = ref([])
    const selectedSizes = ref([])
    const stockWhIndex = ref(0)
    // matrix: { [warehouseId]: { [color+size]: qtyStr } }
    const stockMatrix = ref({})

    const skuKey = (c, s) => (c || '') + '' + (s || '')

    const toggleColor = (val) => {
      const i = selectedColors.value.indexOf(val)
      if (i >= 0) selectedColors.value.splice(i, 1)
      else selectedColors.value.push(val)
    }
    const toggleSize = (val) => {
      const i = selectedSizes.value.indexOf(val)
      if (i >= 0) selectedSizes.value.splice(i, 1)
      else selectedSizes.value.push(val)
    }

    const skuRows = computed(() => {
      if (!selectedColors.value.length && !selectedSizes.value.length) return []
      const colors = selectedColors.value.length ? selectedColors.value : ['']
      const sizes = selectedSizes.value.length ? selectedSizes.value : ['']
      const wh = warehouseOptions.value[stockWhIndex.value]
      const whId = wh ? wh.id : null
      const matrix = (stockMatrix.value && stockMatrix.value[whId]) || {}
      const rows = []
      colors.forEach(c => sizes.forEach(s => {
        const key = skuKey(c, s)
        const label = [c, s].filter(Boolean).join(' / ') || '默认规格'
        const raw = matrix[key]
        rows.push({ color: c, size: s, key, label, qty: raw == null || raw === '' ? null : Number(raw) })
      }))
      return rows
    })

    const onSkuQty = (idx, value) => {
      const row = skuRows.value[idx]
      if (!row) return
      const wh = warehouseOptions.value[stockWhIndex.value]
      const whId = wh ? wh.id : null
      if (whId == null) return
      if (!stockMatrix.value[whId]) stockMatrix.value[whId] = {}
      stockMatrix.value[whId][row.key] = value == null ? '' : String(value)
    }

    // 提交时把矩阵展开成后端 stockItems（含 qty=0）
    const buildStockItems = () => {
      if (!selectedColors.value.length && !selectedSizes.value.length) return []
      const colors = selectedColors.value.length ? selectedColors.value : ['']
      const sizes = selectedSizes.value.length ? selectedSizes.value : ['']
      const items = []
      warehouseOptions.value.forEach(wh => {
        const m = stockMatrix.value[wh.id] || {}
        colors.forEach(c => sizes.forEach(s => {
          const q = Number(m[skuKey(c, s)]) || 0
          items.push({ warehouseId: wh.id, color: c || '', size: s || '', stockQuantity: q })
        }))
      })
      return items
    }
    
    const beforeUpload = async (file) => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        message.error('只能上传图片文件!')
        return false
      }
      
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        message.error('图片大小不能超过 5MB!')
        return false
      }
      
      try {
        const result = await uploadApi.uploadImage(file)
        console.log('图片上传结果:', result)
        if (result.data && result.data.url) {
          form.imageUrls.push(result.data.url)
          console.log('当前图片列表:', form.imageUrls)
        } else {
          message.error('图片上传失败：无效的响应数据')
          console.error('响应结构异常:', result)
        }
        return false
      } catch (error) {
        console.error('图片上传失败:', error)
        message.error(`图片上传失败: ${error.message || '未知错误'}`)
        return false
      }
    }
    
    const handlePreview = (file) => {
      previewImage.value = file.url || file.response?.url
      previewVisible.value = true
      previewTitle.value = file.name || file.url.substring(file.url.lastIndexOf('/') + 1)
    }
    
    const handleRemove = (file) => {
      const index = fileList.value.findIndex(item => item.uid === file.uid)
      if (index > -1) {
        form.imageUrls.splice(index, 1)
      }
    }
    
    const handleSubmit = async (values) => {
      loading.value = true
      try {
        // 处理分类值 - 级联选择器返回数组，取最后一个值（叶子节点）
        let categoryId = values.categoryId
        if (Array.isArray(categoryId) && categoryId.length > 0) {
          categoryId = categoryId[categoryId.length - 1]
        }
        
        const data = {
          ...values,
          categoryId: categoryId,
          imageUrls: form.imageUrls || [],
          // 用矩阵展开后的全量 SKU 覆盖 form.stockItems（含 qty=0）
          stockItems: buildStockItems()
        }

        console.log('提交数据:', data)
        await productApi.create(data)
        message.success('商品创建成功')
        router.push('/products')
      } catch (error) {
        console.error('创建失败:', error)
        message.error(`创建失败: ${error.message || '未知错误'}`)
      } finally {
        loading.value = false
      }
    }
    
    // 分类变化处理
    const onCategoryChange = (value) => {
      console.log('分类变化:', value)
    }
    
    // 总库存：跨所有仓库、仅累计当前已选规格组合
    const totalStockQuantity = computed(() => {
      if (!selectedColors.value.length && !selectedSizes.value.length) return 0
      const colors = selectedColors.value.length ? selectedColors.value : ['']
      const sizes = selectedSizes.value.length ? selectedSizes.value : ['']
      let total = 0
      warehouseOptions.value.forEach(wh => {
        const m = stockMatrix.value[wh.id] || {}
        colors.forEach(c => sizes.forEach(s => {
          total += Number(m[skuKey(c, s)]) || 0
        }))
      })
      return total
    })
    
    onMounted(() => {
      loadCategories()
      loadWarehouses()
      loadAttributes()
    })
    
    return {
      form,
      rules,
      loading,
      categoryOptions,
      warehouseOptions,
      colorOptions,
      sizeOptions,
      newColorValue,
      newSizeValue,
      handleAddColor,
      handleAddSize,
      fileList,
      previewVisible,
      previewImage,
      previewTitle,
      totalStockQuantity,
      // 矩阵式 SKU 编辑
      selectedColors,
      selectedSizes,
      stockWhIndex,
      skuRows,
      toggleColor,
      toggleSize,
      onSkuQty,
      beforeUpload,
      handlePreview,
      handleRemove,
      handleSubmit,
      onCategoryChange
    }
  }
}
</script>

<style scoped>
.product-create {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.form-card {
  margin-bottom: 16px;
}

.stock-section {
  min-height: 200px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header span {
  font-weight: 500;
}

.empty-stock {
  padding: 40px 0;
}

/* ─── 规格选择（颜色/尺码 chip 多选） ─── */
.spec-section {
  margin-bottom: 8px;
}
.spec-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 8px 0;
}
.spec-label {
  width: 48px;
  font-weight: 500;
  color: #333;
  line-height: 28px;
  flex-shrink: 0;
}
.spec-chips {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.spec-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
}
.spec-chip:hover {
  border-color: #5B57E0;
  color: #5B57E0;
}
.spec-chip.on {
  background: #5B57E0;
  border-color: #5B57E0;
  color: #fff;
}

/* ─── SKU 录入行 ─── */
.sku-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sku-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
}
.sku-row .sku-label {
  flex: 1;
  font-size: 14px;
  color: #333;
}
.sku-row .sku-unit {
  font-size: 13px;
  color: #999;
  width: 24px;
}

.stock-items {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;
}

.stock-item {
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.stock-item:last-child {
  border-bottom: none;
}

.stock-item .ant-form-item {
  margin-bottom: 0;
}

.form-actions {
  text-align: center;
  padding: 24px 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 32px;
}
</style>